import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBlogById,
  createBlog,
  editBlog,
  deleteBlog,
} from "../utils/methods";
import QuillEditor from "../component/QuillEditor";
import UploadImage from "../component/UploadImage";
import InputComp from "../component/InputComp";
import { strings } from "../utils/strings";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import { bloggersArr, categories } from "../utils/constants.js";
import CircularProgress from "@mui/material/CircularProgress";

const BlogEdit = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();
  const [blogData, setBlogData] = useState({});
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [bloggerImageUrl, setBloggerImageUrl] = useState("");
  const [blogImageUrl, setBlogImageUrl] = useState("");
  const [bloggerImageFile, setBloggerImageFile] = useState(null);
  const [blogImageFile, setBlogImageFile] = useState(null);
  const [loading, setLoading] = useState(false); // Add loading state

  useEffect(() => {
    const loadBlogData = async () => {
      if (currentId !== "new") {
        try {
          const blog = await getBlogById(currentId);

          console.log("blog", blog);
          setBlogData(blog);
          setName(blog.name || "");
          setType(blog.type || "");
          setTitle(blog.title || "New Blog");
          setBlogImageUrl(blog.blogImage || "");
          setBloggerImageUrl(blog.bloggerImage || "");
        } catch (error) {
          console.error("Error loading blog:", error);
        }
      }
    };
    loadBlogData();
  }, [currentId]);

  const handleTitleChange = (key, newTitle) => {
    setTitle(newTitle);
    setBlogData((prevData) => ({ ...prevData, [key]: newTitle }));
  };

  const handleInputChange = (field, value) => {
    setBlogData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleDescChange = (key, newValue) => {
    setBlogData((prevData) => ({ ...prevData, [key]: newValue }));
  };

  const handleOptionChange = (key, newValue) => {
    setBlogData((prevData) => ({
      ...prevData,
      [key]: newValue || "",
    }));

    // Update corresponding state if necessary
    switch (key) {
      case "name":
        setName(newValue || "");
        break;
      case "type":
        setType(newValue || "");
        break;
      default:
        break;
    }
  };

  const handleImageUpload = (url, file) => {
    setBlogImageUrl(url);
    setBlogImageFile(file);
    setBlogData((prevData) => ({ ...prevData, blogImage: url }));
  };

  const handleBloggerImageUpload = (url, file) => {
    setBloggerImageUrl(url);
    setBloggerImageFile(file);
    setBlogData((prevData) => ({ ...prevData, bloggerImage: url }));
  };

  const formatTitleToId = (title) => {
    return title.trim().replace(/\s+/g, "-").toLowerCase();
  };

  const handleSave = async () => {
    const fieldsToValidate = [
      {
        key: "title",
        value: blogData.title,
        label: strings["inputTitle"],
        regex: null,
      },
      {
        key: "desc",
        value: blogData.desc,
        label: strings["inputDesc"],
        regex: null,
      },
      {
        key: "type",
        value: type,
        label: strings["inputCategory"],
        regex: null,
      },
      { key: "name", value: name, label: strings["inputBlogger"], regex: null },
      {
        key: "blogImage",
        value: blogData.blogImage,
        label: strings["blogImage"],
        regex: null,
      },
      {
        key: "bloggerImage",
        value: blogData.bloggerImage,
        label: strings["bloggerImage"],
        regex: null,
      },
      {
        key: "template",
        value: blogData.template,
        label: "Blog's Content",
        regex: null,
      },
    ];

    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (!value || (regex && !regex.test("should ", () => {})(value))) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      }
    });

    if (!isFormValid) return;

    setLoading(true); // Set loading to true when saving

    try {
      const newBlogId = formatTitleToId(title);
      if (currentId !== "new" && currentId !== newBlogId) {
        await createBlog({ ...blogData, title }, newBlogId);
        await deleteBlog(currentId);
      } else if (currentId === "new") {
        await createBlog({ ...blogData, title }, newBlogId);
      } else {
        await editBlog(currentId, blogData, blogImageFile, bloggerImageFile);
      }
      navigate(`/blogs`);
    } catch (error) {
      console.error("Error saving blog:", error);
    } finally {
      setLoading(false); // Set loading to false once saving is done
    }
  };

  return (
    <div className="pt-14 overflow-auto  justify-center items-center w-full h-full bg-order px-32">
      <div>
        <p className="text-[36px] font-semibold text-blue-600 text-center">
          {currentId === "new"
            ? `${strings["addBlogTitle"]} ${blogData.title || "New"}`
            : `${strings["EditBlogTitle"]} ${blogData.title}`}
        </p>

        {/* Title */}
        <div className="mt-6">
          <InputComp
            label={strings["inputTitle"]}
            placeHolder={strings["titlePlaceholder"]}
            type="text"
            value={title}
            handleChange={handleTitleChange}
            inputKey="title"
            labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
            classes="h-11 text-color-1 hover:border-black border-1 w-full"
          />
        </div>

        {/* Description */}
        <div className="mt-6">
          <InputComp
            label={strings["inputDesc"]}
            placeHolder={strings["descriptionPlaceholder"]}
            type="text"
            value={blogData.desc || ""}
            handleChange={handleDescChange}
            inputKey="desc"
            labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
            classes="h-11 text-color-1 hover:border-black border-1 w-full"
          />
        </div>

        {/* Type Selector */}
        <div className="mt-5">
          <label className="text-sm font-medium">
            {strings["inputCategory"]}
          </label>
          <Autocomplete
            className="w-full"
            options={categories}
            getOptionLabel={(option) => option || ""}
            value={type}
            onChange={(event, newValue) => handleOptionChange("type", newValue)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>

        {/* Bloggers Name Selector */}
        <div className="mt-6">
          <div className="mb-[6px]">
            <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
              {strings["inputBlogger"]}
            </label>
          </div>
          <Autocomplete
            className="w-full"
            options={bloggersArr}
            getOptionLabel={(option) => option || ""}
            value={name}
            onChange={(event, newValue) => handleOptionChange("name", newValue)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    padding: 0,
                    paddingLeft: 1,
                    borderRadius: "8px",
                    backgroundColor: "white",
                  },
                  "& .MuiInputBase-input": {
                    height: "100%",
                    padding: "0 12px",
                    textAlign: "start",
                    boxSizing: "border-box",
                  },
                }}
              />
            )}
          />
        </div>

        {/* Blog Image */}
        <div className="mt-[20px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["blogImage"]}
          </label>
          <UploadImage
            onImageUpload={handleImageUpload}
            blogId={currentId === "new" ? null : currentId}
            imageType="blogImages"
            initialImageUrl={blogData.blogImage}
          />
        </div>

        {/* Blogger Image */}
        <div className="mt-[20px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["bloggerImage"]}
          </label>
          <UploadImage
            onImageUpload={handleBloggerImageUpload}
            blogId={currentId === "new" ? null : currentId}
            imageType="bloggerImages"
            initialImageUrl={blogData.bloggerImage}
          />
        </div>

        {/* HTML Editor */}
        <div className="my-6 bg-white ">
          <QuillEditor
            value={blogData.template || ""}
            onChange={(content) => handleInputChange("template", content)}
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-center w-full bottom ">
          <button
            className="bg-customblue  hover:shadow-xl hover:shadow-[#6791a0]  text-white font-semibold py-[14px] rounded-2xl w-[70%] "
            onClick={handleSave}
          >
            {loading ? (
              <CircularProgress size={24} color="white" />
            ) : (
              strings["submitButton"]
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogEdit;
